/**
 * usePolls / usePollDetail (ported from flock-expo/src/hooks/usePolls.ts)
 *
 * Same query keys as the Expo app. Differences: no dashboard invalidation
 * (web has no home feed), and every service call pins the owning org's ID
 * so cross-org rail requests can't race the org switch.
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pollsService, type PollUpdates } from '@/services/polls.service';
import { useAuth } from '@/context/AuthContext';
import type { Poll, CreatePollPayload } from '@flock-types/polls.types';

export interface UsePollsReturn {
  polls: Poll[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createPoll: (data: CreatePollPayload) => Promise<Poll>;
}

export function usePolls(groupId: string | null, organizationId?: string): UsePollsReturn {
  const { currentOrganization, isSwitchingOrg } = useAuth();
  const queryClient = useQueryClient();

  const queryKey = ['polls', groupId];

  const pollsQuery = useQuery({
    queryKey,
    queryFn: async () => {
      const [active, closed, archived] = await Promise.all([
        pollsService.getPolls(groupId!, 'active', organizationId),
        pollsService.getPolls(groupId!, 'closed', organizationId),
        pollsService.getPolls(groupId!, 'archived', organizationId),
      ]);
      return [...active, ...closed, ...archived];
    },
    // Gate on !isSwitchingOrg so refetches during the claims-verification
    // window don't fire with NEW_TOKEN + OLD_GROUP_ID and 403 against the
    // new org's RLS context.
    enabled: !!currentOrganization?.id && !!groupId && !isSwitchingOrg,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreatePollPayload) =>
      pollsService.createPoll(groupId!, data, organizationId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['poll-badges'] });
    },
  });

  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['polls', groupId] });
  }, [queryClient, groupId]);

  return {
    polls: pollsQuery.data ?? [],
    loading: pollsQuery.isLoading,
    error: pollsQuery.error ? 'Failed to load polls' : null,
    refetch,
    createPoll: createMutation.mutateAsync,
  };
}

export interface UsePollDetailReturn {
  poll: Poll | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  vote: (optionIds: string[]) => Promise<void>;
  retractVote: () => Promise<void>;
  addOption: (label: string) => Promise<void>;
  deleteOption: (optionId: string) => Promise<void>;
  updatePoll: (updates: PollUpdates) => Promise<void>;
  deletePoll: () => Promise<void>;
  closePoll: () => Promise<void>;
  reopenPoll: () => Promise<void>;
  extendPoll: (closesAt: string) => Promise<void>;
  isMutating: boolean;
}

export function usePollDetail(
  pollId: string | null,
  organizationId?: string
): UsePollDetailReturn {
  const { currentOrganization, isSwitchingOrg } = useAuth();
  const queryClient = useQueryClient();

  const queryKey = ['poll-detail', pollId];

  const detailQuery = useQuery({
    queryKey,
    queryFn: () => pollsService.getPollDetail(pollId!, organizationId),
    enabled: !!currentOrganization?.id && !!pollId && !isSwitchingOrg,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['poll-detail', pollId] });
    queryClient.invalidateQueries({ queryKey: ['polls'] });
    queryClient.invalidateQueries({ queryKey: ['poll-badges'] });
  }, [queryClient, pollId]);

  const voteMutation = useMutation({
    mutationFn: async (optionIds: string[]) => {
      await pollsService.vote(pollId!, optionIds, organizationId);
    },
    onSettled: invalidate,
  });

  const retractVoteMutation = useMutation({
    mutationFn: async () => {
      await pollsService.retractVote(pollId!, organizationId);
    },
    onSettled: invalidate,
  });

  const addOptionMutation = useMutation({
    mutationFn: async (label: string) => {
      await pollsService.addOption(pollId!, label, organizationId);
    },
    onSettled: invalidate,
  });

  const deleteOptionMutation = useMutation({
    mutationFn: async (optionId: string) => {
      await pollsService.deleteOption(optionId, organizationId);
    },
    onSettled: invalidate,
  });

  const updatePollMutation = useMutation({
    mutationFn: async (updates: PollUpdates) => {
      await pollsService.updatePoll(pollId!, updates, organizationId);
    },
    onSettled: invalidate,
  });

  const deletePollMutation = useMutation({
    mutationFn: async () => {
      await pollsService.deletePoll(pollId!, organizationId);
    },
    onSettled: invalidate,
  });

  const isMutating =
    voteMutation.isPending ||
    retractVoteMutation.isPending ||
    addOptionMutation.isPending ||
    deleteOptionMutation.isPending ||
    updatePollMutation.isPending ||
    deletePollMutation.isPending;

  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['poll-detail', pollId] });
  }, [queryClient, pollId]);

  return {
    poll: detailQuery.data ?? null,
    loading: detailQuery.isLoading,
    error: detailQuery.error ? 'Failed to load poll' : null,
    refetch,
    vote: voteMutation.mutateAsync,
    retractVote: retractVoteMutation.mutateAsync,
    addOption: addOptionMutation.mutateAsync,
    deleteOption: deleteOptionMutation.mutateAsync,
    updatePoll: updatePollMutation.mutateAsync,
    deletePoll: deletePollMutation.mutateAsync,
    closePoll: () => updatePollMutation.mutateAsync({ status: 'closed' }),
    reopenPoll: () => updatePollMutation.mutateAsync({ status: 'active' }),
    extendPoll: (closesAt: string) => updatePollMutation.mutateAsync({ closes_at: closesAt }),
    isMutating,
  };
}
